import type { LabelMappingEntry } from '@communities/content-management/domain';
import { useCallback } from 'react';

/**
 * Pure validation hook for strike configuration and policy data.
 * No API calls - provides reusable validation logic for strike forms.
 */
export function useStrikeConfigValidation() {
  const validateRollingWindow = useCallback((days: number): string | null => {
    if (!Number.isInteger(days)) return 'Rolling window must be a whole number';
    if (days < 1) return 'Rolling window must be at least 1 day';
    if (days > 365) return 'Rolling window cannot exceed 365 days';

    return null;
  }, []);

  const validateLabelMapping = useCallback((mapping: LabelMappingEntry, existingMappings: LabelMappingEntry[]): string | null => {
    if (mapping.points < 1) return 'Points must be at least 1';
    if (mapping.severity < 1 || mapping.severity > mapping.maxSeverity) {
      return `Severity must be between 1 and ${mapping.maxSeverity}`;
    }

    const duplicate = existingMappings.find(m => m.labelId === mapping.labelId && m.severity === mapping.severity);

    if (duplicate) return 'Duplicate label+severity combination';

    return null;
  }, []);

  const validateLabelMappings = useCallback(
    (mappings: LabelMappingEntry[]): string[] => {
      const errors: string[] = [];

      if (mappings.length > 100) {
        errors.push('Maximum 100 label mappings allowed');
      }

      mappings.forEach((mapping, index) => {
        const error = validateLabelMapping(
          mapping,
          mappings.filter((_, i) => i !== index)
        );

        if (error) errors.push(`Mapping ${index + 1}: ${error}`);
      });

      return errors;
    },
    [validateLabelMapping]
  );

  const validateStrikePolicy = useCallback((threshold: number, actions: unknown[], existingThresholds: number[]): string | null => {
    if (!Number.isInteger(threshold) || threshold <= 0) return 'Threshold must be a positive integer';
    if (actions.length === 0) return 'At least one enforcement action is required';
    if (existingThresholds.includes(threshold)) return 'A policy with this threshold already exists';

    return null;
  }, []);

  return {
    validateRollingWindow,
    validateLabelMapping,
    validateLabelMappings,
    validateStrikePolicy,
  };
}
